import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import {
  STATUS_LABEL,
  STATUS_LABEL_KEY,
  useCertificates,
  type CertificateStatus,
} from '@/hooks/use-certificates';
import { useT } from '@/i18n/use-t';
import { cn } from '@/lib/utils';

const FILTERS: { value: 'all' | CertificateStatus; key: string; label: string }[] = [
  { value: 'active', key: 'certificates.filter.active', label: 'Active' },
  { value: 'all', key: 'certificates.filter.all', label: 'All' },
  { value: 'revoked', key: 'certificates.filter.revoked', label: 'Revoked' },
  { value: 'expired', key: 'certificates.filter.expired', label: 'Expired' },
];

function statusVariant(s: CertificateStatus) {
  switch (s) {
    case 'active':
      return 'success' as const;
    case 'revoked':
      return 'destructive' as const;
    case 'expired':
    default:
      return 'outline' as const;
  }
}

function formatExpiry(
  notAfter: string | null,
  t: (key: string, bakedEnglish?: string) => string,
): {
  label: string;
  warn: boolean;
} {
  if (notAfter === null) return { label: '—', warn: false };
  const d = new Date(notAfter);
  const now = Date.now();
  const ms = d.getTime() - now;
  if (Number.isNaN(d.getTime())) return { label: notAfter, warn: false };
  if (ms <= 0)
    return {
      label: `${d.toISOString().slice(0, 10)} (${t('certificates.list.expiredSuffix', 'expired')})`,
      warn: true,
    };
  const days = Math.floor(ms / 86_400_000);
  if (days <= 30)
    return {
      label: `${d.toISOString().slice(0, 10)} (${days}${t('certificates.list.daysSuffix', 'd')})`,
      warn: true,
    };
  return { label: d.toISOString().slice(0, 10), warn: false };
}

export function CertificatesListPage() {
  const t = useT('admin_ui');
  const [filter, setFilter] = useState<'all' | CertificateStatus>('active');
  const { data, isLoading } = useCertificates({
    pageSize: 100,
    status: filter === 'all' ? undefined : [filter],
  });
  const items = useMemo(() => data?.items ?? [], [data]);

  return (
    <div className="space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t('certificates.list.title', 'Certificates')}</h1>
          <p className="text-sm text-muted-foreground">
            {t(
              'certificates.list.subtitle',
              'Generic signing / fiscal certificate library. Payloads are encrypted at rest; reveal is admin-only and audit-logged.',
            )}
          </p>
        </div>
        <Button asChild>
          <Link to="/certificates/new">{t('certificates.upload.title', 'Upload certificate')}</Link>
        </Button>
      </header>

      <div className="flex items-center gap-1 rounded-md border border-border bg-card p-1 text-sm">
        {FILTERS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            className={cn(
              'rounded-sm px-3 py-1.5 transition-colors',
              filter === opt.value
                ? 'bg-secondary font-medium text-secondary-foreground'
                : 'text-muted-foreground hover:bg-secondary/60',
            )}
            onClick={() => setFilter(opt.value)}
          >
            {t(opt.key, opt.label)}
          </button>
        ))}
      </div>

      <div className="rounded-md border border-border bg-card">
        {isLoading ? (
          <p className="px-4 py-6 text-sm text-muted-foreground">
            {t('common.loading', 'Loading…')}
          </p>
        ) : items.length === 0 ? (
          <p className="px-4 py-6 text-sm text-muted-foreground">
            {t('certificates.list.emptyLead', 'No certificates match this filter. Click')}{' '}
            <strong>{t('certificates.upload.title', 'Upload certificate')}</strong>{' '}
            {t('certificates.list.emptyTail', 'to add one.')}
          </p>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>{t('certificates.field.label', 'Label')}</TH>
                <TH>{t('certificates.field.kind', 'Kind')}</TH>
                <TH>{t('certificates.list.serial', 'Serial')}</TH>
                <TH>{t('certificates.list.expires', 'Expires')}</TH>
                <TH>{t('certificates.list.boundProfile', 'Bound profile')}</TH>
                <TH>{t('certificates.field.status', 'Status')}</TH>
                <TH className="text-right">{t('certificates.field.actions', 'Actions')}</TH>
              </TR>
            </THead>
            <TBody>
              {items.map((c) => {
                const exp = formatExpiry(c.notAfter, t);
                return (
                  <TR key={c.id}>
                    <TD>{c.label}</TD>
                    <TD className="font-mono text-xs">{c.kind}</TD>
                    <TD className="font-mono text-xs">{c.serialNumber ?? '—'}</TD>
                    <TD className={cn('font-mono text-xs', exp.warn && 'text-destructive')}>
                      {exp.label}
                    </TD>
                    <TD className="font-mono text-xs">{c.terminalProfileId ?? '—'}</TD>
                    <TD>
                      <Badge variant={statusVariant(c.status)}>
                        {t(STATUS_LABEL_KEY[c.status], STATUS_LABEL[c.status])}
                      </Badge>
                    </TD>
                    <TD className="text-right">
                      <Button asChild variant="ghost" size="sm">
                        <Link to={`/certificates/${c.id}`}>
                          {t('certificates.list.open', 'Open')}
                        </Link>
                      </Button>
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>
        )}
      </div>
    </div>
  );
}
